import { Injectable } from '@angular/core';
import { AppointmentStatus } from '@features/web/appointments/domain/models/appointment-status.model';
import { AppointmentDetail } from '../../domain/models/appointment-detail.model';
import { ClientRef } from '../../domain/models/client-ref.model';
import { CreateTaskInput } from '../../domain/models/create-task-input.model';
import { DashboardSummary } from '../../domain/models/dashboard-summary.model';
import { PendingTask } from '../../domain/models/pending-task.model';
import { Professional } from '../../domain/models/professional.model';
import { ReasonType } from '../../domain/models/reason-type.model';
import { UnreadMessage } from '../../domain/models/unread-message.model';
import { UpcomingAppointment } from '../../domain/models/upcoming-appointment.model';
import { AppointmentDetailDto } from '../dtos/appointment-detail.dto';
import { ClientRefDto } from '../dtos/client-ref.dto';
import { DashboardAppointmentDto } from '../dtos/dashboard-appointment.dto';
import { DashboardMessageDto } from '../dtos/dashboard-message.dto';
import { DashboardSummaryDto } from '../dtos/dashboard-summary.dto';
import { ProfessionalDto } from '../dtos/professional.dto';
import { ExternalResourceDto } from '../dtos/reason-type.dto';
import { TaskDto } from '../dtos/task.dto';

@Injectable()
export class DashboardMapper {
  public toDashboardSummary(dto: DashboardSummaryDto): DashboardSummary {
    return {
      organizationId: dto.OrganizationId,
      newClientsTrendPct: dto.NewClientsTrendPct ?? null,
      activeClients: dto.ActiveClients,
      mobileAppClients: dto.MobileAppClients,
      mobileAppClientsTrendPct: dto.MobileAppClientsTrendPct ?? null,
      unreadConversations: dto.UnreadConversations,
      oldestUnreadMinutes: dto.OldestUnreadMinutes ?? null,
      appointmentsToday: dto.AppointmentsToday,
      messages: dto.Messages.map((message) => this.toUnreadMessage(message)),
      tasks: dto.Tasks.map((task) => this.toPendingTask(task)),
      tasksTotalCount: dto.TasksTotalCount,
      upcomingAppointments: dto.UpcomingAppointments.map((appointment) => this.toUpcomingAppointment(appointment)),
    };
  }

  public toPendingTask(dto: TaskDto): PendingTask {
    return {
      id: dto.Id,
      title: dto.Title,
      text: dto.Text ?? null,
      completed: dto.Completed,
      dueDate: dto.DueDate ?? null,
      assignedUserId: dto.AssignedUserId ?? null,
      assignedUserName: dto.AssignedUserName ?? null,
      clientId: dto.ClientId ?? null,
      clientName: dto.ClientName ?? null,
      patientIds: dto.PatientIds ?? [],
      patientNames: dto.PatientNames ?? [],
      createdAtUtc: dto.CreatedAtUtc,
      isAssignedToMe: dto.IsAssignedToMe,
    };
  }

  // Shared by both create (POST /api/tasks) and update (PATCH /api/tasks/{id}) -- ProVet's task
  // fields are lowercase (title/text/due/user/client/patients); ApplyTaskHyperlinks server-side
  // turns the bare user/client/patients ids into full Provet URIs. Optional/empty fields are
  // omitted entirely rather than sent as `undefined`/empty, so a PATCH never blanks out a field
  // the caller didn't intend to change.
  public toCreateTaskPayload(input: CreateTaskInput): Record<string, unknown> {
    const payload: Record<string, unknown> = { title: input.title };

    if (input.text) payload['text'] = input.text;
    if (input.dueDate) payload['due'] = input.dueDate;
    if (input.assignedUserId) payload['user'] = input.assignedUserId;
    if (input.clientId) payload['client'] = input.clientId;
    if (input.patientIds?.length) payload['patients'] = input.patientIds;

    return payload;
  }

  public toToggleTaskPayload(completed: boolean): Record<string, unknown> {
    return { status: completed ? 2 : 1 };
  }

  public toReasonType(dto: ExternalResourceDto): ReasonType {
    return {
      id: dto.ExternalId,
      displayName: dto.DisplayName ?? null,
      color: this.resolveReasonTypeColor(dto.Attributes),
      durationMinutes: this.resolveReasonTypeDurationMinutes(dto.Attributes),
    };
  }

  public toAppointmentDetail(dto: AppointmentDetailDto): AppointmentDetail {
    return {
      id: dto.ExternalId,
      clientId: dto.ExternalClientId ?? null,
      patientIds: dto.ExternalPatientIds ?? [],
      professionalId: dto.ExternalProfessionalId ?? null,
      start: dto.Start ?? null,
      end: dto.End ?? null,
      complaint: dto.Complaint ?? null,
      status: this.resolveStatus(dto.Status),
      type: dto.Type ?? null,
      departmentId: dto.DepartmentId ?? null,
      reasonTypeId: dto.ReasonTypeId ?? null,
      title: dto.Title ?? null,
      notes: dto.Notes ?? null,
    };
  }

  public toProfessional(dto: ProfessionalDto): Professional {
    return {
      id: dto.ExternalId,
      firstName: dto.FirstName ?? null,
      lastName: dto.LastName ?? null,
      displayName: dto.DisplayName ?? null,
      email: dto.Email ?? null,
      isActive: dto.IsActive,
      avatarUrl: dto.PictureUrl ?? null,
      departmentId: dto.DepartmentId ?? null,
    };
  }

  public toClientRef(dto: ClientRefDto): ClientRef {
    return {
      id: dto.ExternalId,
      fullName: this.resolveFullName(dto),
      phone: dto.Phone ?? null,
      patients: (dto.Patients ?? []).map((patient) => ({
        id: patient.ExternalId,
        name: patient.Name,
        speciesId: patient.SpeciesId ?? null,
        breedId: patient.BreedId ?? null,
        dateOfBirth: patient.DateOfBirth ?? null,
      })),
    };
  }

  private toUnreadMessage(dto: DashboardMessageDto): UnreadMessage {
    return {
      conversationId: dto.ConversationId,
      clientId: dto.ClientId,
      clientName: dto.ClientName,
      initials: dto.Initials,
      preview: dto.Preview,
      ageMinutes: dto.AgeMinutes,
    };
  }

  private toUpcomingAppointment(dto: DashboardAppointmentDto): UpcomingAppointment {
    return {
      id: dto.Id,
      start: dto.Start ?? null,
      reasonTypeId: dto.ReasonTypeId ?? null,
      status: this.resolveStatus(dto.Status ?? null),
      patientName: dto.PatientName,
      speciesId: dto.SpeciesId ?? null,
      ownerName: dto.OwnerName,
      patientPictureUrl: dto.PatientPictureUrl ?? null,
    };
  }

  // Mirrors AppointmentMapper.resolveStatus in the appointments feature exactly: falls back to
  // Programada for a missing status AND for the retired `1` (Confirmada), which no longer matches
  // any member of the enum.
  private resolveStatus(value: number | null): AppointmentStatus {
    if (value == null || !(value in AppointmentStatus)) return AppointmentStatus.Programada;
    return value as AppointmentStatus;
  }

  private resolveFullName(dto: ClientRefDto): string {
    const fullName = dto.FullName?.trim();
    if (fullName) return fullName;

    const combined = [dto.FirstName, dto.LastName].filter(Boolean).join(' ').trim();
    if (combined) return combined;

    return dto.Email ?? '';
  }

  // ProVet's exact casing for the reason-type color attribute isn't confirmed, so this does a
  // case-insensitive lookup across the raw Attributes bag and defensively normalizes the value:
  // trims whitespace, and prefixes bare 6/8-digit hex values (e.g. "3a64e3") with "#" since some
  // providers omit it. Anything empty or unparseable resolves to null so the UI can fall back to
  // a neutral accent instead of rendering an invalid CSS color. Mirrors
  // AppointmentMapper.resolveReasonTypeColor in the appointments feature.
  private resolveReasonTypeColor(attributes: Readonly<Record<string, string | null>> | null | undefined): string | null {
    if (!attributes) return null;

    const key = Object.keys(attributes).find((attributeKey) => attributeKey.toLowerCase() === 'color');
    const raw = key ? attributes[key] : null;
    if (!raw) return null;

    const trimmed = raw.trim();
    if (!trimmed) return null;

    const bareHexPattern = /^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/;
    if (bareHexPattern.test(trimmed)) return `#${trimmed}`;

    return trimmed;
  }

  // Mirrors AppointmentMapper.resolveReasonTypeDurationMinutes in the appointments feature.
  private resolveReasonTypeDurationMinutes(attributes: Readonly<Record<string, string | null>> | null | undefined): number | null {
    if (!attributes) return null;

    const key = Object.keys(attributes).find((attributeKey) => attributeKey.toLowerCase() === 'duration');
    const raw = key ? attributes[key] : null;
    if (!raw) return null;

    const minutes = Number(raw);
    if (Number.isNaN(minutes) || minutes <= 0) return null;

    return minutes;
  }
}
